import * as Enumerable from 'linq';
import { UserType } from '../../entities';
import { EmployeeAdministrator } from '../roles/employee.administrator';
import { EmployeeCompliance } from '../roles/employee.compliance';
import { EmployeeFinance } from '../roles/employee.finance';
import { EmployeeOperations } from '../roles/employee.operations';
import { EmployeeScheduler } from '../roles/employee.scheduler';
import { EmployeeTechnician } from '../roles/employee.technician';
import { SystemAdministrator } from '../roles/system.administrator';
import { SecurityModule } from '../security.module';
import { SecurityRole, SecurityRoleTiny, SecurityRoleType } from '../security.role';
import { SubcontractorAdministrator, SubcontractorUser, ClientUser } from '../roles';
import { AppSecurityRight } from '../modules';

export class SecurityRoleUtility {

    public static getSystemRoles(): SecurityRole[] {

        const roles = [
            new EmployeeAdministrator(),
            new EmployeeCompliance(),
            new EmployeeFinance(),
            new EmployeeOperations(),
            new EmployeeScheduler(),
            new EmployeeTechnician(),
            new SubcontractorAdministrator(),
            new SubcontractorUser(),
            new SystemAdministrator(),
            new ClientUser()
        ];

        return roles;
    }

    public static isSubContractor(userType: UserType | string): boolean {

        if (!userType) return false;

        const subcontractors = [
            UserType.SubcontractorCompany,
            UserType.SubcontractorEmployee,
            UserType.SubcontractorIndividual
        ] as string[];

        return subcontractors.includes(userType.toString());

    }

    public static isTechnician(roleName: string): boolean {

        if (!roleName) return false;

        const technicianRoles = [
            SecurityRoleType.SubcontractorAdministrator,
            SecurityRoleType.SubcontractorUser,
            SecurityRoleType.EmployeeTechnician
        ] as string[];

        return technicianRoles.includes(roleName);
    }

    public static checkRights(role: SecurityRole, rights: number[]): boolean {

        if (!role || !rights?.length)
            return false;

        const isTiny = !role['moduleRights'];

        const allRights = Enumerable.from(role.rights)
            .distinct()
            .toArray();

        if (!allRights?.length || !rights?.length)
            return false;

        let count = 0;
        Enumerable.from(allRights).forEach(x => {
            if (rights.includes(x))
                count += 1;
        });

        return count > 0;
    }

    public static hasAccessRight(module: SecurityModule<any>, rights: number[]): boolean {

        const accessRight = Enumerable.from(module.rights)
            .select(x => x._id)
            .firstOrDefault();

        return rights.includes(accessRight);
    }

    public static getEmployeeRoles(roles: SecurityRole[]): Partial<SecurityRole>[] {

        return Enumerable.from(roles)
            .where(x => x.type === UserType.Employee)
            .orderBy(x => x.name)
            .toArray();

    }

    public static getSubcontractorRoles(roles: SecurityRole[]): Partial<SecurityRole>[] {

        return Enumerable.from(roles)
            .where(x => x.type !== UserType.Employee && x.type !== UserType.SystemAdministrator)
            .orderBy(x => x.name)
            .toArray();

    }


    public static checkTinyRights(role: SecurityRoleTiny, ...rights: number[]): boolean {

        if (!role || !rights?.length)
            return false;

        return Enumerable.from(role.rights).any(x => rights.includes(x));
    }

    public static checkAllTinyRights(role: SecurityRoleTiny, ...rights: number[]): boolean {

        if (!role || !rights?.length)
            return false;

        return Enumerable.from(rights).all(x => role.rights.includes(x));
    }

    public static getTinyRole(role: SecurityRole): SecurityRoleTiny {
        if (!role) return null;

        return {
            _id: role._id,
            name: role.name,
            type: role.type,
            rights: [
                ...role.rights,
                AppSecurityRight.FeatureSupervisor,
                AppSecurityRight.FeatureScheduler2,
                AppSecurityRight.FeatureJobRoles,
                AppSecurityRight.FeatureBillTo
            ]
        } as SecurityRoleTiny;
    }

}
